import React, {useEffect, useMemo, useState} from "react";

import cx from "classnames";
import {get, groupBy} from "lodash";
import moment from "moment-timezone";

import useUpcomingOrders from "@globals/DailyOrders/useUpcomingOrders";
import {EventOrigin, recordEvent} from "@globals/config/eventUtils";
import {HolidayDetails} from "@globals/hooks/useMenuDates";
import useUpcomingFavorites from "@globals/hooks/useUpcomingFavorites";
import {DateString} from "@globals/types/utilityTypes";
import {useUser} from "@globals/user";

import NomiconIcon from "@components/NomiconIcon";

import styles from "./DailyDateSelector.scss";
import DateSelectorItem from "./DateSelectorItem/DateSelectorItem";

const dateSelectorButtonMobileHeightBasic = 49;
const dateSelectorButtonMobileHeightLarge = 65;

interface DailyDateSelectorProps {
  selectedDate?: DateString;
  menuDates: DateString[];
  selectedLocationId?: number;
  closedDates: DateString[];
  holidayDetails: HolidayDetails;
}

export default function DailyDateSelector({
  selectedDate,
  menuDates,
  selectedLocationId,
  closedDates,
  holidayDetails,
}: DailyDateSelectorProps): JSX.Element {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const {favorites, reset: resetDailyFavorites} = useUpcomingFavorites();
  const {user} = useUser();

  const {ordersGroupedByDate} = useUpcomingOrders();

  const findOrdersForDate = (date: DateString) => {
    return ordersGroupedByDate?.find((group) => group.date === date)
      ?.groupedData;
  };

  const favoritesGroups = useMemo(() => {
    return groupBy(favorites, (item) => get(item, "menu.date"));
  }, [favorites]);

  useEffect(() => {
    if (selectedLocationId) {
      resetDailyFavorites();
    }
  }, [selectedLocationId, user?.favorite_items]);

  const getRelativeDay = (date: DateString): number => {
    // from start of the day
    const timezone = user?.selected_location.timezone || "UTC";
    const today = moment().tz(timezone).startOf("day");

    const dateMoment = moment.tz(date, timezone);
    return dateMoment.diff(today, "days");
  };

  const handleDateClick = (date: DateString) => {
    // close the dropdown on mobile view;
    setIsOpen(false);

    const daysAhead = getRelativeDay(date);
    recordEvent(EventOrigin.PAGE, "view menu day", daysAhead);
  };

  const onToggle = (): void => setIsOpen(!isOpen);

  const getHeight = () => {
    const daysWithOrder = menuDates
      .map((date) => findOrdersForDate(date))
      .filter((val) => val).length;
    const largeDateButtonsHeight =
      (closedDates.length + daysWithOrder) *
      dateSelectorButtonMobileHeightLarge;
    const basicDateButtonHeight =
      (menuDates.length - (closedDates.length + daysWithOrder)) *
      dateSelectorButtonMobileHeightBasic;

    return largeDateButtonsHeight + basicDateButtonHeight;
  };

  const mobileDropdownHeight = useMemo(
    () => (menuDates.length ? getHeight() : 0),
    [menuDates, closedDates, ordersGroupedByDate]
  );

  return (
    <>
      <button onClick={onToggle} className={styles.mobileOnlyToggleBtn}>
        <span className={styles.toggleBtnContent}>
          {selectedDate && moment(selectedDate, "YYYY-MM-DD").format("ddd M/D")}
        </span>
        &nbsp;
        <NomiconIcon icon="nomicon-caret-down" />
      </button>
      <div
        style={{
          minHeight: `${isOpen ? mobileDropdownHeight : 0}px`,
        }}
      >
        <div className={cx(styles.dateItems, isOpen && styles.isOpen)}>
          {menuDates.map((date) => (
            <DateSelectorItem
              holidayDetails={holidayDetails[date]}
              closedDates={closedDates}
              favoritesCount={favoritesGroups[date]?.length}
              key={date}
              date={date}
              orders={findOrdersForDate(date)}
              isSelected={date === selectedDate}
              onDateClick={() => handleDateClick(date)}
            />
          ))}
        </div>
      </div>
    </>
  );
}
